/**
 * Module: WalletScreen/HistorySection
 * Role: Renders the earnings history list in 4 states: loading (3 skeleton rows),
 *       populated (HistoryRow per item), empty (stub copy), error (honest empty).
 *       Split from WalletScreen.tsx to keep parent under 400 LOC cap (ADR architecture).
 *
 * Uses:
 *   - components/atoms: EmptyState, Skeleton, Ic
 *   - styles/tokens: P (palette), S (semantic), T (typography)
 *   - lib/format: formatKopecks, formatTxType, formatRelativeDate
 *
 * Used by:
 *   - screens/WalletScreen (default export WalletScreen)
 * Glossary: ai/glossary/buyer.md
 */
import { EmptyState, Skeleton, Ic } from '../../components/atoms';
import { P, S, T } from '../../styles/tokens';
import { formatKopecks, formatTxType, formatRelativeDate } from '../../lib/format';

// ─── Backend contract (SSOT: src/api/v2/buyer/schemas.py EarningsHistoryItem) ─
export interface EarningsHistoryItem {
  id: string;
  type: string;           // TransactionType StrEnum lowercase (credit, withdraw, refund, adjustment)
  amount_kopecks: number; // Signed ledger delta; UI renders sign by type and formats abs amount (DA-5)
  reference_type: string | null;
  reference_id: string | null;
  description: string | null;
  created_at: string;     // ISO 8601 datetime
}

interface HistorySectionProps {
  isPending: boolean;
  isError: boolean;
  items: EarningsHistoryItem[];
}

// ─── HistoryRow ───────────────────────────────────────────────────────────────
// Sign convention: withdraw → "−" (Unicode minus U+2212); all others → "+".
// Backend may return negative debit amounts; format abs value to avoid a double minus.
// Prototype wallet: icon tile (30×30, r:7) + body text + footnote date + headline amount.

function HistoryRow({ item, isLast }: { item: EarningsHistoryItem; isLast: boolean }) {
  const isDebit = item.type === 'withdraw';
  const sign = isDebit ? '−' : '+';
  const amountColor = isDebit ? S.label : P.success;
  // Title preference: server-curated description → localised type fallback (DA-4).
  const title = item.description ?? formatTxType(item.type);

  // Icon tile colours: withdraw = muted fill / others = soft green
  const tileBg = isDebit ? 'rgba(45,37,24,0.10)' : 'rgba(45,138,78,0.18)';

  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', minHeight: 52, position: 'relative' }}>
      {/* Icon tile — 30×30, r:7, prototype wallet rows */}
      <div style={{
        width: 30, height: 30, borderRadius: 7, background: tileBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, color: isDebit ? S.secondaryLabel : P.success,
      }}>
        {isDebit
          ? <Ic name="arrowUR" size={16} color={S.secondaryLabel} />
          : <Ic name="check" size={16} color={P.success} />
        }
      </div>

      {/* Text block */}
      <div style={{ flex: 1, minWidth: 0, marginLeft: 12 }}>
        <p style={{
          font: T.bodyText, color: S.label, margin: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{title}</p>
        <p style={{ font: T.footnote, color: S.secondaryLabel, margin: '2px 0 0' }}>
          {formatRelativeDate(item.created_at)}
        </p>
      </div>

      {/* Amount */}
      <span style={{ font: T.headline, color: amountColor, flexShrink: 0 }}>
        {sign}{formatKopecks(Math.abs(item.amount_kopecks))}
      </span>

      {/* Internal separator — prototype: left: 58 (tile 30 + gap 12 + padding 16), right: 0 */}
      {!isLast && (
        <div style={{
          position: 'absolute', bottom: 0, left: 58, right: 0,
          height: 0.5, background: S.separator,
        }} />
      )}
    </div>
  );
}

// ─── HistorySection ───────────────────────────────────────────────────────────
// Prototype: InsetList = white bg, r:20, overflow hidden, internal separators.
// 4 visual states: loading / error / empty / populated.

export function HistorySection({ isPending, isError, items }: HistorySectionProps) {
  // Loading — 3 skeleton rows in InsetList container
  if (isPending) {
    return (
      <div style={{ background: S.secondaryGroupedBg, borderRadius: 20, overflow: 'hidden' }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center',
            padding: '10px 16px', minHeight: 52, gap: 12, position: 'relative',
          }}>
            <Skeleton h={30} w={30} style={{ borderRadius: 7, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <Skeleton h={16} w={140} style={{ marginBottom: 6 }} />
              <Skeleton h={12} w={60} />
            </div>
            <Skeleton h={16} w={70} />
            {i < 2 && (
              <div style={{ position: 'absolute', bottom: 0, left: 58, right: 0,
                height: 0.5, background: S.separator }} />
            )}
          </div>
        ))}
      </div>
    );
  }

  // Error — honest empty state (DA-7)
  if (isError) {
    return <EmptyState title="Не удалось загрузить историю" />;
  }

  // Empty — unified wallet history copy (DA-6)
  if (items.length === 0) {
    return <EmptyState title="Здесь появятся начисления и выводы" />;
  }

  // Populated — InsetList with HistoryRow items
  return (
    <div style={{ background: S.secondaryGroupedBg, borderRadius: 20, overflow: 'hidden' }}>
      {items.map((item, i) => (
        <HistoryRow key={item.id} item={item} isLast={i === items.length - 1} />
      ))}
    </div>
  );
}
